import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardBody,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  FormGroup,
} from "reactstrap";
import { toast } from "react-toastify";
import SysAdminService from "../../services/sysadmin";

// ───────────────────────────────────────────────
// קבועים
// ───────────────────────────────────────────────
const DAY_NAMES = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];
const MONTH_NAMES = [
  "ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני",
  "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר",
];

// צבעים לפי KindOfDayId
const KIND_COLORS = {
  2: { bg: "#fff3cd", border: "#f0ad4e", label: "חג" },        // חג — צהוב-כתום
  3: { bg: "#ffeaa7", border: "#fdcb6e", label: "ערב חג" },    // ערב חג — צהוב
  4: { bg: "#d4edda", border: "#28a745", label: "חול המועד" }, // חול המועד — ירוק
};

const SHABBAT_COLOR = { bg: "#e9ecef", border: "#ced4da" };

// ───────────────────────────────────────────────
// Component
// ───────────────────────────────────────────────
function CalendarPage() {
  const today = new Date();
  const [year,  setYear]  = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1-based

  const [calendarMap, setCalendarMap] = useState({}); // { "yyyy-MM-dd": { id, kindOfDayId, note, ... } }
  const [kindOfDayList, setKindOfDayList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // modal
  const [modalOpen,      setModalOpen]      = useState(false);
  const [selectedDate,   setSelectedDate]   = useState(null); // "yyyy-MM-dd"
  const [selectedKind,   setSelectedKind]   = useState(1);
  const [selectedNote,   setSelectedNote]   = useState("");
  const [isSaving,       setIsSaving]       = useState(false);

  // ── טעינה ──────────────────────────────────
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [calRes, kindRes] = await Promise.all([
        SysAdminService.getCalendarMonth(year, month),
        SysAdminService.getKindOfDay(),
      ]);

      // מיפוי תאריך → נתון
      const map = {};
      (calRes.data || []).forEach((d) => { map[d.date] = d; });
      setCalendarMap(map);
      setKindOfDayList(kindRes.data || []);
    } catch {
      toast.error("שגיאה בטעינת לוח השנה");
    } finally {
      setIsLoading(false);
    }
  }, [year, month]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── ניווט חודש ────────────────────────────
  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear((y) => y - 1); }
    else setMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear((y) => y + 1); }
    else setMonth((m) => m + 1);
  };

  // ── לחיצה על יום ─────────────────────────
  const handleDayClick = (dateStr) => {
    const existing = calendarMap[dateStr];
    setSelectedDate(dateStr);
    setSelectedKind(existing?.kindOfDayId ?? 2);
    setSelectedNote(existing?.note ?? "");
    setModalOpen(true);
  };

  // ── שמירה ─────────────────────────────────
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await SysAdminService.upsertCalendarDay({
        date:        selectedDate,
        kindOfDayId: selectedKind,
        note:        selectedNote,
        addToAverage: selectedKind === 1, // יום חול → מכניסים לממוצע
        factorToAverage: 1.0,
      });
      toast.success("נשמר בהצלחה");
      setModalOpen(false);
      await loadData();
    } catch {
      toast.error("שגיאה בשמירה");
    } finally {
      setIsSaving(false);
    }
  };

  // ── מחיקה (ניקוי סימון) ──────────────────
  const handleClear = async () => {
    const existing = calendarMap[selectedDate];
    if (!existing?.id) { setModalOpen(false); return; }
    setIsSaving(true);
    try {
      await SysAdminService.deleteCalendarDay(existing.id);
      toast.success("הסימון הוסר");
      setModalOpen(false);
      await loadData();
    } catch {
      toast.error("שגיאה במחיקה");
    } finally {
      setIsSaving(false);
    }
  };

  // ── בניית תאי הלוח ────────────────────────
  const buildCalendarCells = () => {
    const firstDay = new Date(year, month - 1, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(year, month, 0).getDate();

    const cells = [];

    // תאים ריקים לפני תחילת החודש
    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`empty-${i}`} style={styles.emptyCell} />);
    }

    // תאי ימים
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const dayOfWeek = (firstDay + day - 1) % 7; // 0=Sun … 6=Sat
      const isShabbat = dayOfWeek === 6;
      const entry = calendarMap[dateStr];
      const kind = entry ? KIND_COLORS[entry.kindOfDayId] : null;
      const isToday = dateStr === formatDate(today);

      const bgColor = isShabbat
        ? SHABBAT_COLOR.bg
        : kind?.bg ?? "#ffffff";
      const borderColor = isShabbat
        ? SHABBAT_COLOR.border
        : isToday
        ? "#2c5de5"
        : kind?.border ?? "#dee2e6";

      cells.push(
        <div
          key={dateStr}
          onClick={() => !isShabbat && handleDayClick(dateStr)}
          style={{
            ...styles.dayCell,
            backgroundColor: bgColor,
            border: `2px solid ${borderColor}`,
            cursor: isShabbat ? "default" : "pointer",
            boxShadow: isToday ? "0 0 0 2px #2c5de5" : "none",
          }}
        >
          <div style={styles.dayNumber}>{day}</div>
          {kind && !isShabbat && (
            <div style={{ ...styles.kindBadge, backgroundColor: kind.border, color: "#fff" }}>
              {kind.label}
            </div>
          )}
          {isShabbat && (
            <div style={{ ...styles.kindBadge, backgroundColor: "#adb5bd", color: "#fff" }}>
              שבת
            </div>
          )}
          {entry?.note && !isShabbat && (
            <div style={styles.noteText} title={entry.note}>
              {entry.note.length > 10 ? entry.note.substring(0, 10) + "…" : entry.note}
            </div>
          )}
        </div>
      );
    }

    return cells;
  };

  return (
    <React.Fragment>
      {/* כותרת */}
      <div className="page-title-box d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0">לוח שנה — ימי שבת וחג</h4>
        <small className="text-muted">סמן ימי חג / ערב חג לסינון הודעות מסופים שאינם פועלים</small>
      </div>

      {/* מקרא */}
      <div className="d-flex gap-3 mb-3 flex-wrap">
        {Object.entries(KIND_COLORS).map(([id, c]) => (
          <span key={id} style={{ ...styles.legendBadge, backgroundColor: c.bg, border: `1px solid ${c.border}` }}>
            {c.label}
          </span>
        ))}
        <span style={{ ...styles.legendBadge, backgroundColor: SHABBAT_COLOR.bg, border: `1px solid ${SHABBAT_COLOR.border}` }}>
          שבת (אוטומטי)
        </span>
      </div>

      <Card>
        <CardBody>
          {/* ניווט חודש */}
          <div className="d-flex align-items-center justify-content-between mb-3">
            <Button color="light" size="sm" onClick={prevMonth}>
              <i className="ri-arrow-right-s-line" /> הקודם
            </Button>
            <h5 className="mb-0 fw-bold">
              {MONTH_NAMES[month - 1]} {year}
            </h5>
            <Button color="light" size="sm" onClick={nextMonth}>
              הבא <i className="ri-arrow-left-s-line" />
            </Button>
          </div>

          {isLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <div style={styles.calendarGrid}>
              {/* כותרות ימים */}
              {DAY_NAMES.map((name, i) => (
                <div
                  key={name}
                  style={{
                    ...styles.headerCell,
                    backgroundColor: i === 6 ? "#adb5bd" : "#f8f9fa",
                    color: i === 6 ? "#fff" : "#495057",
                  }}
                >
                  {name}
                </div>
              ))}
              {/* תאי ימים */}
              {buildCalendarCells()}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Modal עריכת יום */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} centered>
        <ModalHeader toggle={() => setModalOpen(false)}>
          סימון יום — {selectedDate}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>סוג יום</Label>
            <Input
              type="select"
              value={selectedKind}
              onChange={(e) => setSelectedKind(parseInt(e.target.value))}
            >
              {kindOfDayList.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.description}
                </option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>הערה (אופציונלי)</Label>
            <Input
              type="text"
              value={selectedNote}
              onChange={(e) => setSelectedNote(e.target.value)}
              placeholder="לדוגמה: ראש השנה"
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" /> : "שמור"}
          </Button>
          {calendarMap[selectedDate]?.id && (
            <Button color="danger" outline onClick={handleClear} disabled={isSaving}>
              נקה סימון
            </Button>
          )}
          <Button color="secondary" outline onClick={() => setModalOpen(false)}>
            ביטול
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
}

// ───────────────────────────────────────────────
// עזרים
// ───────────────────────────────────────────────
function formatDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ───────────────────────────────────────────────
// סגנונות
// ───────────────────────────────────────────────
const styles = {
  calendarGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: "4px",
    direction: "rtl",
  },
  headerCell: {
    textAlign: "center",
    padding: "8px 4px",
    fontWeight: "600",
    fontSize: "13px",
    borderRadius: "4px",
  },
  dayCell: {
    minHeight: "72px",
    borderRadius: "6px",
    padding: "4px 6px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    transition: "transform 0.1s",
  },
  emptyCell: {
    minHeight: "72px",
  },
  dayNumber: {
    fontWeight: "600",
    fontSize: "15px",
    color: "#343a40",
    lineHeight: "1.2",
  },
  kindBadge: {
    fontSize: "10px",
    padding: "1px 6px",
    borderRadius: "10px",
    marginTop: "4px",
    fontWeight: "600",
  },
  noteText: {
    fontSize: "10px",
    color: "#6c757d",
    marginTop: "2px",
    wordBreak: "break-word",
  },
  legendBadge: {
    padding: "3px 10px",
    borderRadius: "12px",
    fontSize: "13px",
    fontWeight: "500",
  },
};

export default CalendarPage;
